import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";

import Step1Menu from "./steps/Step1Menu";
import Step2ChoosePath from "./steps/Step2ChoosePath";
import StepStaticImport from "./steps/StepStaticImport";
import Step2Products from "./steps/Step2Products";
import Step3Choices from "./steps/Step3Choices";
import Step4Items from "./steps/Step4Items";
import Step5CategoriesSubcategories from "./steps/Step5CategoriesSubcategories";
import Step6Preview from "./steps/Step6Preview";
import WizardLivePhone from "./WizardLivePhone";
import WizardStepper from "./WizardStepper";
import {
  getChoiceDisplayTitle,
  getItemDisplayTitle,
  getPrimaryMenuLanguage,
  getPrimaryMenuName,
  getProductDisplayDescription,
  getProductDisplayTitle,
} from "./steps/utils";
import {
  clearWizardDraft,
  computeWizardProgressPct,
  fileToDataUrl,
  hasStoredWizardDraft,
  loadWizardDraft,
  mergeWizardDraft,
  saveWizardDraft,
} from "./draftStorage";

function mapDraftChoiceToLivePreview(choice, draft, language) {
  if (!choice) return null;
  const choiceId = choice.tempId;
  const itemIds = draft?.relations?.choiceItems?.[choiceId] || [];
  const itemsById = new Map((draft?.items || []).map((item) => [item.tempId, item]));
  return {
    id: choiceId,
    title: getChoiceDisplayTitle(choice, language),
    obligatory: choice.obligatory === true || Number(choice.minSelected) > 0,
    minSelected: Number(choice.minSelected) || 0,
    maxSelected: Number(choice.maxSelected) || 0,
    items: itemIds
      .map((itemId) => itemsById.get(itemId))
      .filter(Boolean)
      .map((item) => ({
        id: item.tempId,
        title: getItemDisplayTitle(item, language),
        price: item.price ?? 0,
        havePrice: item.havePrice !== false,
        secondaryPrice: item.secondaryPrice,
      })),
  };
}

function untitledProductDetail(choices) {
  return {
    title: "",
    description: "",
    price: 0,
    hidePrice: true,
    photoUrl: null,
    choices: Array.isArray(choices) ? choices : [],
  };
}

function mergeChoicePreviewWithProduct(choicePreview, product, photoUrl, language, draft) {
  const formChoices = Array.isArray(choicePreview?.choices) ? choicePreview.choices : [];
  const formChoice = formChoices[0] || null;
  const editingChoiceId = choicePreview?.editingChoiceId || null;
  const assignedIds = product
    ? draft?.relations?.productChoices?.[product.tempId] || []
    : [];
  const choicesById = new Map((draft?.choices || []).map((choice) => [choice.tempId, choice]));

  const choices = [];
  assignedIds.forEach((choiceId) => {
    if (editingChoiceId && choiceId === editingChoiceId) {
      if (formChoice) choices.push({ ...formChoice, id: choiceId });
      return;
    }
    const mapped = mapDraftChoiceToLivePreview(choicesById.get(choiceId), draft, language);
    if (mapped) choices.push(mapped);
  });

  // New choice in the form, or editing a choice not assigned to this product yet.
  // Only preview on products selected in the assign picker. Untitled draft only
  // appears when that product still has no choices.
  const formAssignIds = Array.isArray(choicePreview?.assignProductIds)
    ? choicePreview.assignProductIds
    : [];
  if (formChoice && (!editingChoiceId || !assignedIds.includes(editingChoiceId))) {
    const targeted = !product || formAssignIds.includes(product.tempId);
    const isUntitled = !String(formChoice.title || "").trim();
    if (targeted && (!isUntitled || assignedIds.length === 0)) {
      choices.push(formChoice);
    }
  }

  if (!product) {
    return untitledProductDetail(choices.length ? choices : formChoice ? [formChoice] : []);
  }

  return {
    title: getProductDisplayTitle(product, language),
    description: getProductDisplayDescription(product, language) || "",
    price: product.price ?? 0,
    secondaryPrice: product.secondaryPrice,
    photoUrl: photoUrl || null,
    hidePrice: false,
    choices,
  };
}

function buildLiveFormItem(itemPreview) {
  if (!itemPreview?.formItem) return null;
  const formItem = itemPreview.formItem;
  return {
    id: formItem.id || itemPreview.editingItemId || "preview-item",
    title: formItem.title || "",
    price: formItem.price ?? 0,
    havePrice: formItem.havePrice !== false,
    secondaryPrice: formItem.secondaryPrice,
  };
}

function injectFormItemIntoChoice(mapped, formItem, editingItemId, alwaysInclude = false) {
  if (!mapped) return null;
  let items = Array.isArray(mapped.items) ? [...mapped.items] : [];
  if (!formItem) return { ...mapped, items };

  const existingIdx = editingItemId
    ? items.findIndex((item) => item.id === editingItemId)
    : -1;
  if (existingIdx >= 0) {
    items[existingIdx] = formItem;
  } else if (alwaysInclude || items.length === 0 || String(formItem.title || "").trim()) {
    items = [...items.filter((item) => item.id !== formItem.id), formItem];
  }
  return { ...mapped, items };
}

function mergeItemPreviewWithProduct(itemPreview, product, photoUrl, language, draft) {
  const formItem = buildLiveFormItem(itemPreview);
  const editingItemId = itemPreview?.editingItemId || null;
  const formAssignChoiceIds = new Set(
    Array.isArray(itemPreview?.assignChoiceIds) ? itemPreview.assignChoiceIds : []
  );
  const assignedChoiceIds = product
    ? draft?.relations?.productChoices?.[product.tempId] || []
    : [];
  const choicesById = new Map((draft?.choices || []).map((choice) => [choice.tempId, choice]));
  const placeholderChoice = {
    id: "preview-choice",
    title: "",
    obligatory: false,
    minSelected: 0,
    maxSelected: 1,
    items: formItem ? [formItem] : [],
  };

  if (!product) {
    const untitledChoices = [];
    formAssignChoiceIds.forEach((choiceId) => {
      const mapped = injectFormItemIntoChoice(
        mapDraftChoiceToLivePreview(choicesById.get(choiceId), draft, language),
        formItem,
        editingItemId,
        true
      );
      if (mapped) untitledChoices.push(mapped);
    });
    return untitledProductDetail(untitledChoices.length ? untitledChoices : [placeholderChoice]);
  }

  const choices = [];
  assignedChoiceIds.forEach((choiceId) => {
    const mapped = mapDraftChoiceToLivePreview(choicesById.get(choiceId), draft, language);
    if (!mapped) return;

    if (formItem && formAssignChoiceIds.has(choiceId)) {
      choices.push(injectFormItemIntoChoice(mapped, formItem, editingItemId));
      return;
    }

    let items = Array.isArray(mapped.items) ? [...mapped.items] : [];
    if (editingItemId) {
      items = items.filter((item) => item.id !== editingItemId);
    }
    choices.push({ ...mapped, items });
  });

  return {
    title: getProductDisplayTitle(product, language),
    description: getProductDisplayDescription(product, language) || "",
    price: product.price ?? 0,
    secondaryPrice: product.secondaryPrice,
    photoUrl: photoUrl || null,
    hidePrice: false,
    choices,
  };
}

function mergeLivePreviewWithProduct(candidate, product, photoUrl, language, draft, mode) {
  if (mode === "item") {
    return mergeItemPreviewWithProduct(candidate, product, photoUrl, language, draft);
  }
  return mergeChoicePreviewWithProduct(candidate, product, photoUrl, language, draft);
}

const INTERACTIVE_CONFIRM_STEP = 6;
const STATIC_CONFIRM_STEP = 3;

const initialDraft = {
  wizardPath: null,
  visualSettingsMode: "recommended",
  menu: {
    primaryLanguage: "en",
    optionalLanguages: [],
    menuDetails: [{ language: "en", menuName: "" }],
    menuContentType: "interactive",
    staticMenuMediaType: null,
    productImagesDisabled: false,
    productPricesHidden: false,
    searchBarHidden: false,
    summaryButtonEnabled: false,
  },
  restaurant: {
    menuMode: "view",
    serviceType: "Self Service",
    clientMenuShowLogo: true,
    clientWelcomeScreenEnabled: true,
    clientWelcomeVideoEnabled: true,
    clientShowAllMenusOnOnePage: false,
    clientShowClaimButton: true,
    clientMenuVoiceEnabled: false,
    clientShowWelcomeContactButton: true,
    clientShowStorytellingButton: true,
    clientShowEventsButton: true,
    clientShowReservationButton: true,
  },
  products: [],
  choices: [],
  items: [],
  categories: [],
  subcategories: [],
  relations: {
    productChoices: {},
    choiceItems: {},
    categoryProducts: {},
    categorySubcategories: {},
    subcategoryProducts: {},
    menuCategories: [],
  },
};

function collectAssignedProductIds(relations) {
  const assigned = new Set();
  Object.values(relations?.categoryProducts || {}).forEach((ids) => {
    (ids || []).forEach((id) => assigned.add(id));
  });
  Object.values(relations?.subcategoryProducts || {}).forEach((ids) => {
    (ids || []).forEach((id) => assigned.add(id));
  });
  return assigned;
}

function readSavedWizardState(initialDraft) {
  const saved = loadWizardDraft();
  if (!saved) return null;

  let activeStep = typeof saved.activeStep === "number" ? saved.activeStep : 0;
  let wizardPath =
    saved.wizardPath === "static" || saved.wizardPath === "interactive" ? saved.wizardPath : null;

  if (!saved.migratedTo9Steps) {
    if (!saved.migratedTo8Steps) {
      if (activeStep >= 1) activeStep += 1;
      if (activeStep >= 4) activeStep += 2;
      if (activeStep >= 2 && !wizardPath) wizardPath = "interactive";
    }

    if (wizardPath !== "static" && activeStep >= 3) {
      activeStep -= 1;
    }
  }

  if (!saved.migratedConfirmIntoPreview) {
    if (wizardPath === "static" && activeStep >= 4) {
      activeStep = 3;
    } else if (wizardPath !== "static" && activeStep >= 8) {
      activeStep = 7;
    }
  }

  // Categories moved before products: 2↔5, then products/choices/items shift +1.
  if (!saved.migratedCategoriesBeforeProducts && wizardPath !== "static") {
    const categoriesBeforeProductsMap = {
      2: 3,
      3: 4,
      4: 5,
      5: 2,
    };
    if (categoriesBeforeProductsMap[activeStep] != null) {
      activeStep = categoriesBeforeProductsMap[activeStep];
    }
  }

  // Link products folded into create products (old 8-step → 7-step).
  if (!saved.migratedLinkIntoProducts && wizardPath !== "static") {
    if (activeStep === 6) {
      // Was link step → send back to products so placement can be set/reviewed.
      activeStep = 3;
    } else if (activeStep >= 7) {
      activeStep = 6;
    }
  }

  const maxStep = wizardPath === "static" ? STATIC_CONFIRM_STEP : INTERACTIVE_CONFIRM_STEP;
  activeStep = Math.min(Math.max(activeStep, 0), maxStep);

  return {
    draft: mergeWizardDraft(saved.draft, initialDraft),
    activeStep,
    wizardPath,
    menuPhotoPreview: saved.menuPhotoDataUrl || null,
    menuPhotoDataUrl: saved.menuPhotoDataUrl || null,
    restaurantLogoPreview: saved.restaurantLogoDataUrl || null,
    restaurantLogoDataUrl: saved.restaurantLogoDataUrl || null,
    staticMenuPreview: saved.staticMenuDataUrl || null,
    staticMenuDataUrl: saved.staticMenuDataUrl || null,
    productPhotos: saved.productPhotos && typeof saved.productPhotos === "object" ? saved.productPhotos : {},
    subcategoryBanners:
      saved.subcategoryBanners && typeof saved.subcategoryBanners === "object"
        ? saved.subcategoryBanners
        : {},
    migratedTo8Steps: true,
    migratedTo10Steps: true,
    migratedTo9Steps: true,
    migratedConfirmIntoPreview: true,
    migratedCategoriesBeforeProducts: true,
    migratedLinkIntoProducts: true,
  };
}

function isDraftMeaningful(draft) {
  const menuName = getPrimaryMenuName(draft?.menu);
  return (
    menuName.trim().length > 0 ||
    (Array.isArray(draft?.products) && draft.products.length > 0) ||
    (Array.isArray(draft?.categories) && draft.categories.length > 0)
  );
}

function getConfirmStepIndex(wizardPath) {
  return wizardPath === "static" ? STATIC_CONFIRM_STEP : INTERACTIVE_CONFIRM_STEP;
}

const StartNowWizard = () => {
  const { t } = useTranslation();

  const interactiveSteps = useMemo(
    () => [
      { label: t("wizardStepCreateMenu"), icon: "fas fa-book-open" },
      { label: t("wizardStepChoosePath"), icon: "fas fa-route" },
      { label: t("wizardStepCategoriesSubcategories"), icon: "fas fa-sitemap" },
      { label: t("wizardStepCreateProducts"), icon: "fas fa-hamburger" },
      { label: t("wizardStepChoices"), icon: "fas fa-list-check" },
      { label: t("wizardStepItems"), icon: "fas fa-utensils" },
      { label: t("wizardStepPreviewAll"), icon: "fas fa-eye" },
    ],
    [t]
  );

  const staticSteps = useMemo(
    () => [
      { label: t("wizardStepCreateMenu"), icon: "fas fa-book-open" },
      { label: t("wizardStepChoosePath"), icon: "fas fa-route" },
      { label: t("wizardStepStaticImport"), icon: "fas fa-file-upload" },
      { label: t("wizardStepPreviewAll"), icon: "fas fa-eye" },
    ],
    [t]
  );

  const savedState = useMemo(() => readSavedWizardState(initialDraft), []);

  const [activeStep, setActiveStep] = useState(() => savedState?.activeStep ?? 0);
  const [wizardPath, setWizardPath] = useState(() => savedState?.wizardPath ?? null);
  const [draft, setDraft] = useState(() => savedState?.draft ?? initialDraft);
  const [confirmedMenuId, setConfirmedMenuId] = useState(null);
  const [menuPhotoFile, setMenuPhotoFile] = useState(null);
  const [menuPhotoPreview, setMenuPhotoPreview] = useState(() => savedState?.menuPhotoPreview ?? null);
  const [menuPhotoDataUrl, setMenuPhotoDataUrl] = useState(() => savedState?.menuPhotoDataUrl ?? null);
  const [restaurantLogoFile, setRestaurantLogoFile] = useState(null);
  const [restaurantLogoPreview, setRestaurantLogoPreview] = useState(
    () => savedState?.restaurantLogoPreview ?? null
  );
  const [restaurantLogoDataUrl, setRestaurantLogoDataUrl] = useState(
    () => savedState?.restaurantLogoDataUrl ?? null
  );
  const [staticMenuFile, setStaticMenuFile] = useState(null);
  const [staticMenuPreview, setStaticMenuPreview] = useState(() => savedState?.staticMenuPreview ?? null);
  const [staticMenuDataUrl, setStaticMenuDataUrl] = useState(() => savedState?.staticMenuDataUrl ?? null);
  const [productPhotos, setProductPhotos] = useState(() => savedState?.productPhotos ?? {});
  const [subcategoryBanners, setSubcategoryBanners] = useState(
    () => savedState?.subcategoryBanners ?? {}
  );
  const [liveDetailCandidate, setLiveDetailCandidate] = useState(null);
  const [livePreviewProductId, setLivePreviewProductId] = useState(null);
  const [livePreviewView, setLivePreviewView] = useState("detail");
  const [lastSavedAt, setLastSavedAt] = useState(() => loadWizardDraft()?.savedAt ?? null);
  const [draftRestored, setDraftRestored] = useState(() => !!savedState);
  const saveTimerRef = useRef(null);
  const menuPhotoFileRef = useRef(null);
  const restaurantLogoFileRef = useRef(null);
  const staticMenuFileRef = useRef(null);

  const fullSteps = wizardPath === "static" ? staticSteps : interactiveSteps;
  const confirmStepIndex = getConfirmStepIndex(wizardPath);

  const collapsedStepper = useMemo(
    () => [
      { label: t("wizardStepCreateMenu"), stepIndex: 0 },
      { label: t("wizardStepPreviewAll"), stepIndex: confirmStepIndex },
    ],
    [t, confirmStepIndex]
  );

  const stepperSteps = wizardPath
    ? fullSteps.map((step, idx) => ({ ...step, stepIndex: idx }))
    : collapsedStepper;

  const menuName = getPrimaryMenuName(draft?.menu);
  const isMenuComplete = menuName.trim().length > 0;
  const isChooseComplete = !!wizardPath;
  const productsList = Array.isArray(draft?.products) ? draft.products : [];
  const assignedProductCount = collectAssignedProductIds(draft?.relations || {}).size;
  const isProductsComplete =
    productsList.length > 0 && assignedProductCount === productsList.length;
  const isChoicesComplete = true;
  const isItemsComplete = true;
  const isCategoriesComplete = Array.isArray(draft?.categories) && draft.categories.length > 0;
  const isStaticImportComplete = !!(staticMenuFile || staticMenuPreview || staticMenuDataUrl);
  const isPreviewComplete =
    wizardPath === "static"
      ? isMenuComplete && isStaticImportComplete
      : isMenuComplete && isProductsComplete && isCategoriesComplete;

  const interactiveStepComplete = [
    isMenuComplete,
    isChooseComplete,
    isCategoriesComplete,
    isProductsComplete,
    isChoicesComplete,
    isItemsComplete,
    isPreviewComplete,
  ];

  const staticStepComplete = [
    isMenuComplete,
    isChooseComplete,
    isStaticImportComplete,
    isPreviewComplete,
  ];

  const stepComplete = !wizardPath
    ? [isMenuComplete, isPreviewComplete]
    : wizardPath === "static"
      ? staticStepComplete
      : interactiveStepComplete;

  const doneUpToIndex = stepComplete.reduce((acc, ok, idx) => (ok ? idx : acc), -1);
  const progressPct = computeWizardProgressPct(stepComplete);
  const showDraftActions = isDraftMeaningful(draft) || hasStoredWizardDraft();

  useEffect(() => {
    menuPhotoFileRef.current = menuPhotoFile;
  }, [menuPhotoFile]);

  useEffect(() => {
    restaurantLogoFileRef.current = restaurantLogoFile;
  }, [restaurantLogoFile]);

  useEffect(() => {
    staticMenuFileRef.current = staticMenuFile;
  }, [staticMenuFile]);

  useEffect(() => {
    if (!menuPhotoFile && !menuPhotoPreview) {
      setMenuPhotoDataUrl(null);
      return undefined;
    }
    if (menuPhotoPreview?.startsWith("data:")) {
      setMenuPhotoDataUrl(menuPhotoPreview);
    }
    if (!menuPhotoFile) return undefined;
    let cancelled = false;
    fileToDataUrl(menuPhotoFile)
      .then((url) => {
        if (!cancelled) setMenuPhotoDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setMenuPhotoDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [menuPhotoFile, menuPhotoPreview]);

  useEffect(() => {
    if (!restaurantLogoFile && !restaurantLogoPreview) {
      setRestaurantLogoDataUrl(null);
      return undefined;
    }
    if (restaurantLogoPreview?.startsWith("data:")) {
      setRestaurantLogoDataUrl(restaurantLogoPreview);
    }
    if (!restaurantLogoFile) return undefined;
    let cancelled = false;
    fileToDataUrl(restaurantLogoFile)
      .then((url) => {
        if (!cancelled) setRestaurantLogoDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setRestaurantLogoDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantLogoFile, restaurantLogoPreview]);

  useEffect(() => {
    if (!staticMenuFile && !staticMenuPreview) {
      setStaticMenuDataUrl(null);
      return undefined;
    }
    if (staticMenuPreview?.startsWith("data:")) {
      setStaticMenuDataUrl(staticMenuPreview);
    }
    if (!staticMenuFile) return undefined;
    let cancelled = false;
    fileToDataUrl(staticMenuFile)
      .then((url) => {
        if (!cancelled) setStaticMenuDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setStaticMenuDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [staticMenuFile, staticMenuPreview]);

  useEffect(() => {
    if (confirmedMenuId) return undefined;
    if (
      !isDraftMeaningful(draft) &&
      activeStep === 0 &&
      !menuPhotoDataUrl &&
      !restaurantLogoDataUrl &&
      !staticMenuDataUrl
    ) {
      return undefined;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const ok = saveWizardDraft({
        draft: { ...draft, wizardPath },
        activeStep,
        menuPhotoDataUrl,
        restaurantLogoDataUrl,
        productPhotos,
        subcategoryBanners,
        staticMenuDataUrl,
        wizardPath,
        migratedTo8Steps: true,
        migratedTo10Steps: true,
        migratedTo9Steps: true,
        migratedConfirmIntoPreview: true,
        migratedCategoriesBeforeProducts: true,
        migratedLinkIntoProducts: true,
      });
      if (ok) setLastSavedAt(Date.now());
    }, 500);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [
    draft,
    activeStep,
    menuPhotoDataUrl,
    restaurantLogoDataUrl,
    productPhotos,
    subcategoryBanners,
    staticMenuDataUrl,
    wizardPath,
    confirmedMenuId,
  ]);

  useEffect(() => {
    if (!draftRestored) return undefined;
    const timer = setTimeout(() => setDraftRestored(false), 5000);
    return () => clearTimeout(timer);
  }, [draftRestored]);

  useEffect(() => {
    const mainwrapper = document.querySelector("#main-wrapper");
    if (!mainwrapper) return undefined;

    const wasCompact = mainwrapper.classList.contains("menu-toggle");
    const desktopQuery = window.matchMedia("(min-width: 992px)");

    const applySidebarMode = () => {
      if (desktopQuery.matches) {
        mainwrapper.classList.add("menu-toggle");
      } else {
        mainwrapper.classList.remove("menu-toggle");
      }
    };

    const closeMobileSidebar = (event) => {
      if (desktopQuery.matches) return;
      if (!mainwrapper.classList.contains("menu-toggle")) return;
      if (
        event.target.closest(".deznav") ||
        event.target.closest(".nav-control") ||
        event.target.closest(".nav-header")
      ) {
        return;
      }
      mainwrapper.classList.remove("menu-toggle");
    };

    document.body.classList.add("start-now-wizard-active");
    applySidebarMode();

    desktopQuery.addEventListener("change", applySidebarMode);
    document.addEventListener("click", closeMobileSidebar);

    return () => {
      document.body.classList.remove("start-now-wizard-active");
      desktopQuery.removeEventListener("change", applySidebarMode);
      document.removeEventListener("click", closeMobileSidebar);
      if (wasCompact) {
        mainwrapper.classList.add("menu-toggle");
      } else {
        mainwrapper.classList.remove("menu-toggle");
      }
    };
  }, []);

  const handleDeleteDraft = async () => {
    const confirmed = await swal({
      title: t("wizardDeleteDraftConfirmTitle"),
      text: t("wizardDeleteDraftConfirmText"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    clearWizardDraft();
    if (menuPhotoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(menuPhotoPreview);
    }
    if (restaurantLogoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(restaurantLogoPreview);
    }
    if (staticMenuPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticMenuPreview);
    }
    setDraft(initialDraft);
    setActiveStep(0);
    setWizardPath(null);
    setMenuPhotoFile(null);
    setMenuPhotoPreview(null);
    setMenuPhotoDataUrl(null);
    setRestaurantLogoFile(null);
    setRestaurantLogoPreview(null);
    setRestaurantLogoDataUrl(null);
    setStaticMenuFile(null);
    setStaticMenuPreview(null);
    setStaticMenuDataUrl(null);
    setProductPhotos({});
    setSubcategoryBanners({});
    setConfirmedMenuId(null);
    setLastSavedAt(null);
    setDraftRestored(false);
  };

  useEffect(() => {
    const validIds = new Set((draft.products || []).map((p) => p.tempId));
    setProductPhotos((prev) => {
      const next = {};
      let changed = false;
      Object.entries(prev || {}).forEach(([id, url]) => {
        if (validIds.has(id)) {
          next[id] = url;
        } else {
          changed = true;
        }
      });
      if (!changed && Object.keys(next).length === Object.keys(prev || {}).length) {
        return prev;
      }
      return next;
    });
  }, [draft.products]);

  useEffect(() => {
    const validIds = new Set((draft.subcategories || []).map((s) => s.tempId));
    setSubcategoryBanners((prev) => {
      const next = {};
      let changed = false;
      Object.entries(prev || {}).forEach(([id, url]) => {
        if (validIds.has(id)) {
          next[id] = url;
        } else {
          changed = true;
        }
      });
      if (!changed && Object.keys(next).length === Object.keys(prev || {}).length) {
        return prev;
      }
      return next;
    });
  }, [draft.subcategories]);

  const goNext = () => {
    const maxStep = wizardPath === "static" ? STATIC_CONFIRM_STEP : INTERACTIVE_CONFIRM_STEP;
    setActiveStep((s) => Math.min(s + 1, maxStep));
  };

  const goPrev = () => {
    setActiveStep((s) => {
      if (s <= 1) return Math.max(s - 1, 0);
      if (s === 2) {
        setWizardPath(null);
        setStaticMenuFile(null);
        setStaticMenuPreview((prev) => {
          if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
          return null;
        });
        setStaticMenuDataUrl(null);
        setDraft((prev) => ({
          ...prev,
          wizardPath: null,
          menu: { ...prev.menu, menuContentType: "interactive", staticMenuMediaType: null },
        }));
      }
      return Math.max(s - 1, 0);
    });
  };

  const stepProps = useMemo(
    () => ({
      draft,
      setDraft,
      goNext,
      goPrev,
      setActiveStep,
      wizardPath,
      setWizardPath,
      menuPhotoFile: menuPhotoFile ?? menuPhotoFileRef.current,
      setMenuPhotoFile,
      menuPhotoPreview,
      setMenuPhotoPreview,
      menuPhotoDataUrl,
      restaurantLogoFile: restaurantLogoFile ?? restaurantLogoFileRef.current,
      setRestaurantLogoFile,
      restaurantLogoPreview,
      setRestaurantLogoPreview,
      restaurantLogoDataUrl,
      staticMenuFile: staticMenuFile ?? staticMenuFileRef.current,
      setStaticMenuFile,
      staticMenuPreview,
      setStaticMenuPreview,
      staticMenuDataUrl,
      productPhotos,
      setProductPhotos,
      subcategoryBanners,
      setSubcategoryBanners,
      setLiveDetailCandidate,
      livePreviewProductId,
      activeStep,
    }),
    [
      draft,
      wizardPath,
      activeStep,
      menuPhotoFile,
      menuPhotoPreview,
      menuPhotoDataUrl,
      restaurantLogoFile,
      restaurantLogoPreview,
      restaurantLogoDataUrl,
      staticMenuFile,
      staticMenuPreview,
      staticMenuDataUrl,
      productPhotos,
      subcategoryBanners,
      livePreviewProductId,
    ]
  );

  const isChoiceOrItemStep =
    wizardPath === "interactive" && (activeStep === 4 || activeStep === 5);

  useLayoutEffect(() => {
    setLivePreviewProductId(null);
    setLivePreviewView("detail");
    if (!isChoiceOrItemStep) {
      setLiveDetailCandidate(null);
    }
  }, [activeStep, wizardPath, isChoiceOrItemStep]);

  const livePreviewMode = isChoiceOrItemStep && activeStep === 5 ? "item" : "choice";

  const liveProductDetail = useMemo(() => {
    if (!isChoiceOrItemStep || livePreviewView !== "detail" || !liveDetailCandidate) {
      return null;
    }
    const language = getPrimaryMenuLanguage(draft?.menu);
    if (!livePreviewProductId) {
      return mergeLivePreviewWithProduct(
        liveDetailCandidate,
        null,
        null,
        language,
        draft,
        livePreviewMode
      );
    }
    const product = (draft?.products || []).find((p) => p.tempId === livePreviewProductId);
    if (!product) return null;
    return mergeLivePreviewWithProduct(
      liveDetailCandidate,
      product,
      productPhotos?.[livePreviewProductId] || null,
      language,
      draft,
      livePreviewMode
    );
  }, [
    isChoiceOrItemStep,
    livePreviewView,
    liveDetailCandidate,
    livePreviewProductId,
    draft,
    productPhotos,
    livePreviewMode,
  ]);

  const renderActiveStep = () => {
    if (wizardPath === "static") {
      if (activeStep === 0) return <Step1Menu {...stepProps} />;
      if (activeStep === 1) return <Step2ChoosePath {...stepProps} />;
      if (activeStep === 2) return <StepStaticImport {...stepProps} />;
      if (activeStep === 3) {
        return (
          <Step6Preview
            {...stepProps}
            onConfirmed={(id) => {
              setConfirmedMenuId(id);
              clearWizardDraft();
              setLastSavedAt(null);
            }}
          />
        );
      }
      return null;
    }

    if (activeStep === 0) return <Step1Menu {...stepProps} />;
    if (activeStep === 1) return <Step2ChoosePath {...stepProps} />;
    if (activeStep === 2) return <Step5CategoriesSubcategories {...stepProps} />;
    if (activeStep === 3) return <Step2Products {...stepProps} />;
    if (activeStep === 4) return <Step3Choices {...stepProps} />;
    if (activeStep === 5) return <Step4Items {...stepProps} />;
    if (activeStep === 6) {
      return (
        <Step6Preview
          {...stepProps}
          onConfirmed={(id) => {
            setConfirmedMenuId(id);
            clearWizardDraft();
            setLastSavedAt(null);
          }}
        />
      );
    }
    return null;
  };

  const stepperActiveIndex = wizardPath
    ? activeStep
    : activeStep >= confirmStepIndex
      ? 1
      : 0;

  const isChoosingPath = !wizardPath && activeStep === 1;
  const isPreviewStep =
    (wizardPath === "static" && activeStep === 3) ||
    (wizardPath !== "static" && activeStep === 6);
  const totalWizardSteps =
    wizardPath === "static" ? STATIC_CONFIRM_STEP + 1 : INTERACTIVE_CONFIRM_STEP + 1;

  return (
    <Fragment>
      <div
        className={`start-now-wizard-page${isPreviewStep ? " start-now-wizard-page--preview" : ""}`}
        data-tour="tour-page-start-now"
      >
        <div className="start-now-wizard-main">
          <div className="card start-now-wizard-card">
            <div className="card-header wizard-card-header">
              <div className="wizard-header">
                <div className="wizard-header__lead">
                  <span className="wizard-header__badge" aria-hidden="true">
                    <i className="fa fa-magic" />
                  </span>
                  <div className="wizard-header__text">
                    <h4 className="wizard-header__title">{t("wizardSetupTitle")}</h4>
                    <p className="wizard-header__sub">{t("wizardSetupSub")}</p>
                  </div>
                </div>

                <div className="wizard-header__side">
                  <span className="wizard-header__pct">{progressPct}%</span>
                  {showDraftActions ? (
                    <button
                      type="button"
                      className="wizard-header__delete"
                      onClick={handleDeleteDraft}
                    >
                      <i className="fa fa-trash-alt" aria-hidden="true" />
                      <span>{t("wizardDeleteDraft")}</span>
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="wizard-header__progress">
                <div
                  className="wizard-header__bar"
                  role="progressbar"
                  aria-label={t("wizardProgressLabel")}
                  aria-valuenow={progressPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="wizard-header__fill" style={{ width: `${progressPct}%` }} />
                </div>
                {draftRestored ? (
                  <span className="wizard-header__status is-restored">
                    <i className="fa fa-history" aria-hidden="true" />
                    {t("wizardDraftRestored")}
                  </span>
                ) : lastSavedAt ? (
                  <span className="wizard-header__status">
                    <i className="fa fa-cloud" aria-hidden="true" />
                    {t("wizardDraftSaved")}
                  </span>
                ) : (
                  <span className="wizard-header__status is-muted">
                    {t("wizardStepperEyebrow", {
                      current: activeStep + 1,
                      total: totalWizardSteps,
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className="card-body wizard-card-body">
              <div className="form-wizard">
                <WizardStepper
                  steps={stepperSteps}
                  activeStep={activeStep}
                  activeVisualIndex={stepperActiveIndex}
                  stepComplete={wizardPath === "static" ? staticStepComplete : interactiveStepComplete}
                  wizardPath={wizardPath}
                  isChoosingPath={isChoosingPath}
                  totalSteps={totalWizardSteps}
                  onStepClick={setActiveStep}
                />

                {renderActiveStep()}
              </div>
            </div>
          </div>
        </div>

        {!isPreviewStep ? (
        <aside className="start-now-wizard-live" aria-label={t("wizardPreviewLive")}>
          <WizardLivePhone
            draft={draft}
            menuPhotoPreview={menuPhotoPreview}
            restaurantLogoPreview={restaurantLogoPreview}
            staticMenuPreview={staticMenuPreview}
            wizardPath={wizardPath}
            productPhotos={productPhotos}
            activeStep={activeStep}
            compact
            showStepLabel={!liveProductDetail}
            productDetail={liveProductDetail}
            showProductClickHint={
              Boolean(liveDetailCandidate) && livePreviewView === "list"
            }
            onProductClick={
              liveDetailCandidate && livePreviewView === "list"
                ? (productId) => {
                    setLivePreviewProductId(productId);
                    setLivePreviewView("detail");
                  }
                : undefined
            }
            onProductDetailBack={
              liveProductDetail
                ? () => {
                    setLivePreviewView("list");
                    setLivePreviewProductId(null);
                  }
                : undefined
            }
          />
        </aside>
        ) : null}
      </div>
    </Fragment>
  );
};

export default StartNowWizard;
